import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { DownloadableBlob } from '@ps/utils';
import { ToastService } from '@ps/core-overlay';

import { CoreMappingsService } from '../../../services/core-mappings.service';
import { CoreMappingsActions } from '../actions/core-mappings.actions';
import { EffectsBase } from '@ps/core-data-access';

@Injectable()
export class CoreMappingsEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(CoreMappingsService);
  private readonly _document = inject(DOCUMENT);
  private readonly _toast = inject(ToastService);

  public readonly loadBuyers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CoreMappingsActions.loadBuyers),
      switchMap(() =>
        this._service.buyers().pipe(
          map((buyers) => CoreMappingsActions.loadBuyersSuccess({ buyers })),
          catchError((error: unknown) =>
            of(CoreMappingsActions.loadBuyersFailure({ error: String(error) })),
          ),
        ),
      ),
    ),
  );

  public readonly uploadMappings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CoreMappingsActions.uploadMappings),
      switchMap(({ file, buyerId }) =>
        this._service.uploadMappings(file, buyerId).pipe(
          tap(() => this._toast.success('Mappings uploaded successfully')),
          map(({ message }) =>
            CoreMappingsActions.uploadMappingsSuccess({ message }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CoreMappingsActions.uploadMappingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while processing your request. Please try again later.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly autoResetAfterUpload$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CoreMappingsActions.uploadMappingsSuccess),
      map(() => CoreMappingsActions.uploadMappingsReset()),
    ),
  );

  public readonly downloadMappings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CoreMappingsActions.downloadMappings),
      switchMap(({ buyerId, fileName }) =>
        this._service.downloadMappings(buyerId).pipe(
          tap((blob) =>
            new DownloadableBlob(blob, fileName, this._document).download(),
          ),
          map(() => CoreMappingsActions.downloadMappingsSuccess()),
          catchError((error: unknown) =>
            of(
              CoreMappingsActions.downloadMappingsFailure({
                error: String(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
